import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { isApiError } from "@/services/Api";
import { caseSecurityService } from "@/services";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useLoggingStore } from "@/stores/logging-store";
import { useBrandSecurityStore } from "@/stores/brand-security-store";

import type { CaseFileSecurity } from "@/services/CaseSecurityService";

interface State {
  moduleStatus: ModuleStatus;
  security: Partial<CaseFileSecurity>;
}

export const useCaseSecurityStore = defineStore("caseSecurity", {
  scope: "case",
  state: (): State => ({
    moduleStatus: ModuleStatus.Initial,
    security: {},
  }),
  getters: {},
  actions: {
    async load(): Promise<void> {
      const brandStore = useBrandStore();
      const brandSecurityStore = useBrandSecurityStore();
      const selectedCaseName = useCaseStore().selectedCaseName;

      if (brandSecurityStore.config?.isCaseSecurityEnabled !== true) return;

      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      try {
        this.setStatus(ModuleStatus.Loading);

        const caseSecurity = await caseSecurityService.get(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
        );
        this.setCaseSecurity(selectedCaseName, caseSecurity);
      } catch (error) {
        useLoggingStore().Error({
          message: "Failed to load case security details",
          data: error as object,
        });
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async addEntry(group: "tags" | "contributors" | "nominatedUsers", entry: string, fromConcurrencyError = false) {
      const brandSecurityStore = useBrandSecurityStore();
      const { selectedBrand, selectedClient } = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;

      if (selectedClient == null || selectedBrand == null) return;
      if (brandSecurityStore.config?.isCaseSecurityEnabled !== true || this.security.concurrency == null)
        throw "Could not add entry.";

      if (selectedCaseName == null) throw "Case not selected";

      const args = [selectedClient, selectedBrand, selectedCaseName, entry, this.security.concurrency] as const;

      try {
        switch (group) {
          case "tags":
            await caseSecurityService.addTag(...args);
            break;
          case "contributors":
            await caseSecurityService.restoreContributor(...args);
            break;
          case "nominatedUsers":
            await caseSecurityService.addNominatedUser(...args);
            break;
        }
      } catch (error) {
        console.error(error);

        if (isApiError(error)) {
          const groupLabels = { tags: "tag", contributors: "contributor", nominatedUsers: "nominated user" } as const;
          if (error.status === 304) throw `No changes made. This ${groupLabels[group]} already exists.`;

          if (error.status === 409) {
            if (!fromConcurrencyError) {
              await this.load();
              await this.addEntry(group, entry, true);
              return;
            } else {
              throw "Could not add/restore entry after repeated tries. The data was changed since your last update.";
            }
          }
        }

        throw typeof error === "string" ? error : "Could not add/restore entry. Unknown error.";
      } finally {
        if (!fromConcurrencyError) this.load();
      }
    },
    async deleteEntry(group: "tags" | "contributors" | "nominatedUsers", entry: string, fromConcurrencyError = false) {
      const brandSecurityStore = useBrandSecurityStore();
      const { selectedBrand, selectedClient } = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;

      if (selectedClient == null || selectedBrand == null) return;
      if (brandSecurityStore.config?.isCaseSecurityEnabled !== true || this.security.concurrency == null)
        throw "Could not add entry.";

      if (selectedCaseName == null) throw "Case not selected";

      const args = [selectedClient, selectedBrand, selectedCaseName, entry, this.security.concurrency] as const;

      try {
        switch (group) {
          case "tags":
            await caseSecurityService.excludeTag(...args);
            break;
          case "contributors":
            await caseSecurityService.excludeContributor(...args);
            break;
          case "nominatedUsers":
            await caseSecurityService.excludeNominatedUser(...args);
            break;
        }
      } catch (error) {
        console.error(error);

        if ((error as { status: number }).status === 409) {
          if (!fromConcurrencyError) {
            await this.load();
            await this.deleteEntry(group, entry, true);
          } else {
            throw "Could not delete/exclude entry after repeated tries";
          }
        } else {
          throw typeof error === "string" ? error : "Could not delete/exclude entry. Unknown error.";
        }
      } finally {
        if (!fromConcurrencyError) this.load();
      }
    },

    setStatus(status: ModuleStatus): void {
      this.moduleStatus = status;
    },
    setCaseSecurity(caseReference: string, payload: CaseFileSecurity): void {
      this.security = payload;
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.security = {};
    },
  },
});
